import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Row, Col, Input, Select, message } from 'antd';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { searchPincodeV2 } from 'store/clientSliceV2';
import AddressPickerV2 from 'components/common/AddressPickerV2/AddressPickerV2';
import loadGoogleMapsV2 from 'utils/loadGoogleMapsV2';
import { truncateAddressToCity } from 'utils/addressHelpers';

const ModalContent = styled.div`
	padding: 16px 0;
`;

const FormLabel = styled.label`
	display: block;
	margin-bottom: 8px;
	font-size: 14px;
	font-weight: 500;
	color: #333;

	.required {
		color: #ff4d4f;
		margin-left: 4px;
	}
`;

const StyledInput = styled(Input)`
	height: 40px;
	border-radius: 4px;
	border: 1px solid #d9d9d9;

	&:focus {
		border-color: #18405d;
		box-shadow: 0 0 0 2px rgba(24, 64, 93, 0.1);
	}
`;

const StyledSelect = styled(Select)`
	width: 100%;

	.ant-select-selector {
		height: 40px !important;
		display: flex;
		align-items: center;
		border-radius: 4px;
		border: 1px solid #d9d9d9;
	}

	&.ant-select-focused .ant-select-selector {
		border-color: #18405d;
		box-shadow: 0 0 0 2px rgba(24, 64, 93, 0.1);
	}
`;

const DisabledInput = styled(StyledInput)`
	background: #f5f5f5;
	color: #000;
	cursor: not-allowed;
`;

const FormGroup = styled.div`
	margin-bottom: 24px;
`;

const RemoveButton = styled.button`
	height: 40px;
	padding: 0 20px;
	border: 1px solid #ff4d4f;
	background: #fff;
	color: #ff4d4f;
	font-size: 14px;
	font-weight: 500;
	border-radius: 4px;
	cursor: pointer;
	transition: all 0.3s;

	&:hover {
		background: #ff4d4f;
		color: #fff;
	}
`;

const SaveButton = styled.button`
	height: 40px;
	padding: 0 20px;
	border: none;
	background: #18405d;
	color: #fff;
	font-size: 14px;
	font-weight: 500;
	border-radius: 4px;
	cursor: pointer;
	transition: all 0.3s;

	&:hover {
		background: #1a4a6e;
	}
`;

const NODE_TYPE_OPTIONS = [
	{ value: 'Warehouse', label: 'Warehouse' },
	{ value: 'Store', label: 'Store' },
];

export default function NodeActionModal({ open, onCancel, nodeData, onEdit, onDelete }) {
	const dispatch = useDispatch();
	const [formData, setFormData] = useState({
		project_name: '',
		node_type: '',
		node_name: '',
		node_address: '',
		latitude: '',
		longitude: '',
		poc_name: '',
		poc_number: '',
		pincode: '',
		zone: '',
		city: '',
		state: '',
	});
	const [pocNumberError, setPocNumberError] = useState('');

	// Extract node_address line1 if it's an object
	const getNodeAddress = (data) => {
		if (!data?.node_address) return '';
		if (typeof data.node_address === 'string') return data.node_address;
		if (typeof data.node_address === 'object' && data.node_address.line1) {
			return data.node_address.line1;
		}
		return '';
	};

	// Load Google Maps on component mount
	useEffect(() => {
		loadGoogleMapsV2().catch((err) => {
			console.error('Failed to load Google Maps:', err);
			message.error('Failed to load Google Maps. Please refresh the page.');
		});
	}, []);

	useEffect(() => {
		if (nodeData) {
			// Extract data from node_address object if available
			const nodeAddressObj = nodeData.node_address;
			const extractedCity = nodeAddressObj?.city || nodeData.city || '';
			const extractedState = nodeAddressObj?.state || nodeData.state || '';
			const extractedPincode = nodeAddressObj?.pincode || nodeData.pincode || '';

			// Get node address and truncate to city when loading existing data
			const nodeAddress = getNodeAddress(nodeData);
			const truncatedAddress = nodeAddress
				? truncateAddressToCity(nodeAddress, extractedCity)
				: '';

			setFormData({
				project_name: nodeData.project_name || '',
				node_type: nodeData.node_type || '',
				node_name: nodeData.node_name || '',
				node_address: truncatedAddress,
				latitude: nodeData.latitude || '',
				longitude: nodeData.longitude || '',
				poc_name: nodeData.poc_name || '',
				poc_number: nodeData.poc_number || '',
				pincode: extractedPincode,
				zone: nodeData.zone || '',
				city: extractedCity,
				state: extractedState,
			});
			// Clear validation error when modal opens with data
			setPocNumberError('');
		}
	}, [nodeData]);

	// Clear validation error when modal closes
	useEffect(() => {
		if (!open) {
			setPocNumberError('');
		}
	}, [open]);

	// Handle pincode lookup - Auto-fetch zone, city, state when user types 6-digit pincode
	const handlePincodeLookup = async (pincode) => {
		if (pincode && pincode.length === 6 && /^\d{6}$/.test(pincode)) {
			try {
				const result = await dispatch(searchPincodeV2({ pincode }));
				if (result?.zone) {
					setFormData((prev) => ({ ...prev, zone: result.zone }));
				}
				if (result?.city) {
					setFormData((prev) => ({ ...prev, city: result.city }));
				}
				if (result?.state) {
					setFormData((prev) => ({ ...prev, state: result.state }));
				}
			} catch (error) {
				// Error already handled by searchPincodeV2
			}
		}
	};

	const handleInputChange = (field, value) => {
		setFormData((prev) => ({
			...prev,
			[field]: value,
		}));

		// Validate PoC number when it changes
		if (field === 'poc_number') {
			validatePocNumber(value);
		}
	};

	// Validate PoC number: 10 digits starting with 6-9
	const validatePocNumber = (value) => {
		if (!value || value.trim() === '') {
			setPocNumberError('');
			return true;
		}

		// Remove any non-digit characters for validation
		const digitsOnly = value.replace(/\D/g, '');

		if (digitsOnly.length !== 10) {
			setPocNumberError('Mobile number must be exactly 10 digits');
			return false;
		}

		if (!/^[6-9]/.test(digitsOnly)) {
			setPocNumberError('Mobile number must start with 6, 7, 8, or 9');
			return false;
		}

		setPocNumberError('');
		return true;
	};

	const handleSave = () => {
		// Validate PoC number before saving
		if (formData.poc_number && !validatePocNumber(formData.poc_number)) {
			message.error('Please fix the PoC Number validation error before saving');
			return;
		}

		// Clean PoC number: remove non-digit characters before saving
		const cleanedPocNumber = formData.poc_number ? formData.poc_number.replace(/\D/g, '') : '';

		onEdit({ ...nodeData, ...formData, poc_number: cleanedPocNumber });
		onCancel();
	};

	const handleRemove = () => {
		onDelete(nodeData.id);
		onCancel();
	};

	return (
		<Modal
			title="Node Information"
			open={open}
			onCancel={onCancel}
			footer={null}
			width={900}
			centered
		>
			<ModalContent>
				{/* Row 1: Project Name | Node Type */}
				<Row gutter={24}>
					<Col span={12}>
						<FormGroup>
							<FormLabel>
								Project Name<span className="required">*</span>
							</FormLabel>
							<DisabledInput value={formData.project_name} disabled />
						</FormGroup>
					</Col>
					<Col span={12}>
						<FormGroup>
							<FormLabel>
								Node Type<span className="required">*</span>
							</FormLabel>
							<StyledSelect
								value={formData.node_type}
								onChange={(value) => handleInputChange('node_type', value)}
								placeholder="Select"
								options={NODE_TYPE_OPTIONS}
							/>
						</FormGroup>
					</Col>
				</Row>

				{/* Row 2: Node Name | Node Address */}
				<Row gutter={24}>
					<Col span={12}>
						<FormGroup>
							<FormLabel>
								Node Name<span className="required">*</span>
							</FormLabel>
							<StyledInput
								value={formData.node_name}
								onChange={(e) => handleInputChange('node_name', e.target.value)}
								placeholder="Eg : Li"
							/>
						</FormGroup>
					</Col>
					<Col span={12}>
						<FormGroup>
							<FormLabel>
								Node Address<span className="required">*</span>
							</FormLabel>
							<AddressPickerV2
								value={formData.node_address || ''}
								latitude={formData.latitude || ''}
								longitude={formData.longitude || ''}
								onChange={(data) => {
									// Truncate address to city (safeguard - AddressPickerV2 already truncates, but ensure consistency)
									const truncatedAddress = truncateAddressToCity(
										data.address,
										data.city || ''
									);
									setFormData((prev) => ({
										...prev,
										node_address: truncatedAddress,
										latitude: data.latitude,
										longitude: data.longitude,
										// Optionally update state and pincode if they exist
										state: data.state || prev.state,
										pincode: data.pincode || prev.pincode,
									}));
									// Auto-fetch zone and city if pincode is valid
									if (
										data.pincode &&
										data.pincode.length === 6 &&
										/^\d{6}$/.test(data.pincode)
									) {
										handlePincodeLookup(data.pincode);
									}
								}}
								placeholder="delhi, Gurgaon, Haryana"
							/>
						</FormGroup>
					</Col>
				</Row>

				{/* Row 3: Latitude and Longitude - auto-populated by AddressPicker */}
				<Row gutter={24}>
					<Col span={12}>
						<FormGroup>
							<FormLabel>
								Latitude<span className="required">*</span>
							</FormLabel>
							<StyledInput
								value={formData.latitude}
								readOnly
								placeholder="Auto-filled from address"
							/>
						</FormGroup>
					</Col>
					<Col span={12}>
						<FormGroup>
							<FormLabel>
								Longitude<span className="required">*</span>
							</FormLabel>
							<StyledInput
								value={formData.longitude}
								readOnly
								placeholder="Auto-filled from address"
							/>
						</FormGroup>
					</Col>
				</Row>

				{/* Row 4: PoC Name | PoC Number */}
				<Row gutter={24}>
					<Col span={12}>
						<FormGroup>
							<FormLabel>PoC Name</FormLabel>
							<StyledInput
								value={formData.poc_name}
								onChange={(e) => handleInputChange('poc_name', e.target.value)}
								placeholder="Eg : AB 01 AB 1234"
							/>
						</FormGroup>
					</Col>
					<Col span={12}>
						<FormGroup>
							<FormLabel>PoC Number</FormLabel>
							<StyledInput
								value={formData.poc_number}
								onChange={(e) => {
									const { value } = e.target;
									// Allow only digits, limit to 10
									const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
									handleInputChange('poc_number', digitsOnly);
								}}
								placeholder="Eg : 9876543210"
								maxLength={10}
							/>
							{pocNumberError && (
								<div
									style={{
										color: '#ff4d4f',
										fontSize: '12px',
										marginTop: '4px',
									}}
								>
									{pocNumberError}
								</div>
							)}
						</FormGroup>
					</Col>
				</Row>

				{/* Row 5: Pincode | Zone | City | State */}
				<Row gutter={24}>
					<Col span={6}>
						<FormGroup>
							<FormLabel>
								Pincode<span className="required">*</span>
							</FormLabel>
							<StyledInput
								value={formData.pincode}
								onChange={(e) => {
									const { value } = e.target;
									handleInputChange('pincode', value);
									handlePincodeLookup(value);
								}}
								placeholder="Eg : 517408"
							/>
						</FormGroup>
					</Col>
					<Col span={6}>
						<FormGroup>
							<FormLabel>Zone</FormLabel>
							<DisabledInput value={formData.zone} disabled placeholder="South" />
						</FormGroup>
					</Col>
					<Col span={6}>
						<FormGroup>
							<FormLabel>City</FormLabel>
							<DisabledInput value={formData.city} disabled placeholder="South" />
						</FormGroup>
					</Col>
					<Col span={6}>
						<FormGroup>
							<FormLabel>State</FormLabel>
							<DisabledInput value={formData.state} disabled placeholder="South" />
						</FormGroup>
					</Col>
				</Row>

				{/* Action Buttons */}
				<Row justify="space-between" style={{ marginTop: 16 }}>
					<RemoveButton onClick={handleRemove}>Remove Node</RemoveButton>
					<SaveButton onClick={handleSave}>Save Changes</SaveButton>
				</Row>
			</ModalContent>
		</Modal>
	);
}

NodeActionModal.propTypes = {
	open: PropTypes.bool.isRequired,
	onCancel: PropTypes.func.isRequired,
	nodeData: PropTypes.object,
	onEdit: PropTypes.func.isRequired,
	onDelete: PropTypes.func.isRequired,
};

NodeActionModal.defaultProps = {
	nodeData: {},
};
